import React from "react";
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "sonner";
import "./App.css";
import "./theme.css";

import LoginPage from "./pages/LoginPage";
import SetupPage from "./pages/SetupPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsListPage from "./pages/ClientsListPage";
import ClientCreatePage from "./pages/ClientCreatePage";
import ClientDetailPage from "./pages/ClientDetailPage";
import { PracticesListPage, PracticeCreatePage, PracticeDetailPage } from "./pages/PracticesPages";
import PracticeEditPage from "./pages/PracticeEditPage";
import ClientEditPage from "./pages/ClientEditPage";
import DocumentsPage from "./pages/DocumentsPage";
import CalendarPage from "./pages/CalendarPage";
import NotificationsPage from "./pages/NotificationsPage";
import BackupPage from "./pages/BackupPage";
import PushSettingsPage from "./pages/PushSettingsPage";
import { UsersAdminPage, AuditPage, SettingsPage, ArchivedPage } from "./pages/AdminPages";
import GdprPage from "./pages/GdprPage";
import { PartnersListPage, PartnerFormPage, PartnerDetailPage } from "./pages/PartnersPages";
import { BandiListPage, BandoFormPage, BandoDetailPage } from "./pages/BandiPages";
import AppLayout from "./layouts/AppLayout";

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="ma-loading" style={{ padding: 40 }}>Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && window.location.pathname !== "/change-password")
    return <Navigate to="/change-password" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children || <Outlet />;
}

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/setup", element: <SetupPage /> },
  { path: "/change-password", element: <Protected><ChangePasswordPage /></Protected> },
  {
    element: <Protected><AppLayout /></Protected>,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "/clients", element: <ClientsListPage /> },
      { path: "/clients/new", element: <ClientCreatePage /> },
      { path: "/clients/:id", element: <ClientDetailPage /> },
      { path: "/clients/:id/edit", element: <ClientEditPage /> },
      { path: "/partners", element: <PartnersListPage /> },
      { path: "/partners/new", element: <PartnerFormPage /> },
      { path: "/partners/:id", element: <PartnerDetailPage /> },
      { path: "/partners/:id/edit", element: <PartnerFormPage edit /> },
      { path: "/bandi", element: <BandiListPage /> },
      { path: "/bandi/new", element: <BandoFormPage /> },
      { path: "/bandi/:id", element: <BandoDetailPage /> },
      { path: "/bandi/:id/edit", element: <BandoFormPage edit /> },
      { path: "/practices", element: <PracticesListPage /> },
      { path: "/practices/new", element: <PracticeCreatePage /> },
      { path: "/practices/:id", element: <PracticeDetailPage /> },
      { path: "/practices/:id/edit", element: <PracticeEditPage /> },
      { path: "/documents", element: <DocumentsPage /> },
      { path: "/calendar", element: <CalendarPage /> },
      { path: "/notifications", element: <NotificationsPage /> },
      { path: "/profile/notifications", element: <PushSettingsPage /> },
      { path: "/admin/users", element: <Protected admin><UsersAdminPage /></Protected> },
      { path: "/admin/audit", element: <Protected admin><AuditPage /></Protected> },
      { path: "/admin/archived", element: <Protected admin><ArchivedPage /></Protected> },
      { path: "/admin/gdpr", element: <Protected admin><GdprPage /></Protected> },
      { path: "/admin/backup", element: <Protected admin><BackupPage /></Protected> },
      { path: "/admin/settings", element: <Protected admin><SettingsPage /></Protected> },
      { path: "/finance", element: <Protected><Navigate to="/practices" replace /></Protected> },
      { path: "/finance/*", element: <Protected><Navigate to="/practices" replace /></Protected> },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <RouterProvider router={router} />
      </AuthProvider>
    </ThemeProvider>
  );
}
